import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Shield, FileText, Clock, ArrowRight } from 'lucide-react';

const TermsPage = () => {
  const sections = [
    {
      title: "Définitions",
      content: [
        "PRIVIA : Société PRIVIA Collection, SAS au capital de 500 000€, immatriculée au RCS de Paris sous le numéro 850 123 456.",
        "Client : Toute personne physique ou morale ayant souscrit aux services PRIVIA.",
        "Villa : Propriété de luxe mise à disposition par PRIVIA pour location temporaire.",
        "Services : Ensemble des prestations proposées par PRIVIA incluant location de villas et services de conciergerie."
      ]
    },
    {
      title: "Objet et Champ d'Application",
      content: [
        "Les présentes conditions générales régissent toute prestation de services fournie par PRIVIA.",
        "Elles sont applicables à tous les clients sans exception.",
        "Toute commande implique l'acceptation pleine et entière de ces conditions.",
        "PRIVIA se réserve le droit de modifier ces conditions à tout moment."
      ]
    },
    {
      title: "Réservations et Paiements",
      content: [
        "Toute réservation doit être confirmée par un acompte de 30% du montant total.",
        "Le solde est exigible 30 jours avant l'arrivée.",
        "Les paiements s'effectuent par virement bancaire ou carte de crédit.",
        "Aucune réservation n'est définitive sans paiement complet dans les délais impartis."
      ]
    },
    {
      title: "Annulations et Modifications",
      content: [
        "Annulation plus de 60 jours avant : remboursement intégral moins 10% de frais de dossier.",
        "Annulation entre 30 et 60 jours : retenue de 50% du montant total.",
        "Annulation moins de 30 jours avant : aucun remboursement.",
        "Les modifications de dates sont soumises à disponibilité et peuvent entraîner des frais supplémentaires."
      ]
    },
    {
      title: "Responsabilités et Assurances",
      content: [
        "Le client s'engage à occuper la villa en bon père de famille.",
        "Toute dégradation sera facturée au client selon un devis d'expert.",
        "PRIVIA recommande fortement la souscription d'une assurance voyage.",
        "La responsabilité de PRIVIA est limitée au montant des prestations facturées."
      ]
    },
    {
      title: "Protection des Données",
      content: [
        "PRIVIA s'engage à protéger les données personnelles de ses clients.",
        "Les données sont utilisées uniquement dans le cadre de la relation commerciale.",
        "Le client dispose d'un droit d'accès, de rectification et de suppression de ses données.",
        "Aucune donnée n'est transmise à des tiers sans accord explicite du client."
      ]
    },
    {
      title: "Droit Applicable",
      content: [
        "Les présentes conditions sont régies par le droit français.",
        "Tout litige relève de la compétence exclusive des tribunaux de Paris.",
        "En cas de différend, une médiation sera privilégiée avant toute action judiciaire.",
        "Les présentes conditions prévalent sur tout autre document commercial."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge 
            variant="outline" 
            className="mb-6 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <FileText className="w-4 h-4 mr-2" />
            Conditions Légales
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Conditions Générales
          </h1>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Cadre légal régissant nos prestations de services luxury et la relation avec nos clients
          </p>

          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Dernière mise à jour : 15 novembre 2024</span>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300">
                <h2 
                  className="text-2xl font-light text-gray-800 mb-6 pb-3 border-b border-gray-200"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {section.title}
                </h2>
                
                <div className="space-y-4">
                  {section.content.map((paragraph, idx) => (
                    <p 
                      key={idx}
                      className="text-gray-600 leading-relaxed"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Questions sur nos Conditions ?
          </h2>
          <p 
            className="text-lg mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Notre équipe juridique est disponible pour clarifier tout point de ces conditions générales
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Nous Contacter
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;